import { useEffect, useState } from "react"
import type { CityLocalization } from "../../shared/types"
import { HeaderImage } from "../../components/HeaderImage"
import { getFlagFromCountryCode, getImageFromStrapiUploads, getLanguageNameFromLocale } from "../../shared/utils"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { GetStaticPaths, GetStaticProps } from "next"
import { useRouter } from "next/router"
import { useTranslation } from "next-i18next"
import Link from "next/link"
import { GET_CITIES } from "../../assets/api/apiCalls"
import { CityContentList } from "../../components/CityContentList"
import { getApolloClient } from '../_app'
import Head from "next/head"
import { pageTitle } from "../../components/misc/titles"

export default function City({ cityData: initialCityData }: { cityData: any }) {
    const client = getApolloClient()
    const [cityData, setCityData] = useState<any>(initialCityData)
    const [languageNotAvailable, setLanguageNotAvailable] = useState<boolean>(false)
    const router = useRouter()
    const name = router.query.name as string
    const locale = router.locale
    const [currentLocale, setCurrentLocale] = useState<string>(locale)
    const [cityLoading, setCityLoading] = useState<boolean>(true)
    const { t } = useTranslation()

    const [origin, setOrigin] = useState('')
    const [href, setHref] = useState('')

    useEffect(() => {
        if (cityData === initialCityData && cityData === null) {
            router.push('/city/not_found')
        }
        if (typeof window !== 'undefined') {
            setOrigin(window.location.origin)
            setHref(window.location.href)
        }
    }, [])

    const fetchCityData = async (cityName: string, locale: string) => {
        try {
            const { data } = await client.query({
                query: GET_CITIES,
                variables: {
                    "locale": locale,
                    "cityName": cityName,
                }
            })

            setCityData(data)
            setCityLoading(false)
        } catch (error) {
            console.error('Error fetching cities:', error)
            setCityData(null)
        }
    }

    useEffect(() => {
        if (name !== undefined && cityData !== null) {
            fetchCityData(name, locale)
        }
    }, [name])

    useEffect(() => {
        setCityLoading(true)
        if (!cityLoading && cityData?.cities?.data?.length === 0) {
            router.push('/city/not_found')
        } else {
            if (cityData?.cities?.data?.length > 0 && locale !== currentLocale) {
                const localizedCity = getLocalizedCity(cityData, locale)
                if (localizedCity !== undefined && localizedCity.toLowerCase() !== name) {
                    setCurrentLocale(locale)
                    fetchCityData(localizedCity, locale)
                    router.push(`/city/${localizedCity.toLowerCase()}`)
                } else {
                    setLanguageNotAvailable(true)
                }
            } else {
                setLanguageNotAvailable(true)
            }
        }
        setCityLoading(false)
    }, [locale])

    const getLocalizedCity = (cityData: any, locale: string): string => {
        const localization = cityData?.cities?.data?.[0]?.attributes?.localizations?.data?.find((item: CityLocalization) => item?.attributes?.locale === locale)
        if (localization === undefined) {
            return undefined
        } else {
            return localization?.attributes?.name
        }
    }

    const checkDifferentPageLanguage = (cityData: any, locale: string): boolean => {
        if (languageNotAvailable) {
            if (cityData?.cities?.data?.[0]?.attributes?.locale !== locale) {
                return true
            }
        }
        return false
    }

    const getPageInOtherLanguages = (cityData: any) => {
        if (!cityData?.cities?.data?.[0]?.attributes?.localizations?.data) {
            return null
        }
    
        return cityData?.cities?.data?.[0]?.attributes?.localizations?.data.map(
            (localization: any) => (
                <link 
                    key={localization.attributes.locale} 
                    rel="alternate" 
                    hrefLang={localization.attributes.locale} 
                    href={`https://explorerstale.com/city/${localization.attributes.name.toLowerCase()}`} 
                />
            )
        )
    }

    const getCanonicalUrl = (cityData: any) => {
        const cityLocale = cityData?.cities?.data?.[0]?.attributes?.locale
        if (cityLocale !== 'it') {
            return `https://explorerstale.com/${cityLocale}/city/${cityData?.cities?.data?.[0]?.attributes?.name.toLowerCase()}`
        } else {
            return `https://explorerstale.com/city/${cityData?.cities?.data?.[0]?.attributes?.name.toLowerCase()}`
        }
    }

    const getMetadata = () => {
        return (
            <Head>
                <title>{cityData?.cities?.data?.[0]?.attributes?.name.charAt(0).toUpperCase() + cityData?.cities?.data?.[0]?.attributes?.name.slice(1).toLowerCase()}</title>
                <meta name="description" content={cityData?.cities?.data?.[0]?.attributes?.description} key="desc" />
                <meta property="og:title" content={`${cityData?.cities?.data?.[0]?.attributes?.name.charAt(0).toUpperCase() + cityData?.cities?.data?.[0]?.attributes?.name.slice(1).toLowerCase()} | Explorer's Tale`} />
                <meta property="og:description" content={cityData?.cities?.data?.[0]?.attributes?.description} />
                <meta property="og:image" content={cityData?.cities?.data?.[0]?.attributes?.cityCover?.data?.attributes?.url} />
                <meta property="og:url" content={getCanonicalUrl(cityData)} />
                <meta property="og:type" content="article" />
                <meta property="og:locale" content={currentLocale} />
                <meta property="og:site_name" content="Explorer's Tale" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@explorerstale" />
                <meta name="twitter:creator" content="@explorerstale" />
                <meta name="twitter:title" content={`${cityData?.cities?.data?.[0]?.attributes?.name.charAt(0).toUpperCase() + cityData?.cities?.data?.[0]?.attributes?.name.slice(1).toLowerCase()} | Explorer's Tale`} />
                <meta name="twitter:description" content={cityData?.cities?.data?.[0]?.attributes?.description} />
                <meta name="twitter:image" content={cityData?.cities?.data?.[0]?.attributes?.cityCover?.data?.attributes?.url} />
                <link rel="canonical" href={getCanonicalUrl(cityData)} />
                <meta name="author" content="Alessio Lucciola" />
                <meta name="robots" content="all" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="content-language" content={currentLocale} />
                <meta name="keywords" content={cityData?.cities?.data?.[0]?.attributes?.name} />
                {getPageInOtherLanguages(cityData)}
                <link rel="alternate" href="https://explorerstale.com" hrefLang="x-default" />
            </Head>
        )
    }

    return (
        <>
            {getMetadata()}
            <HeaderImage image={cityData?.cities?.data?.[0]?.attributes?.cityCover?.data !== null ? getImageFromStrapiUploads(cityData?.cities?.data?.[0]?.attributes?.cityCover?.data?.attributes) : "/assets/no-img.jpg"} alt={cityData?.cities?.data?.attributes?.name+`-cover`} country={cityData?.cities?.data?.[0]?.attributes?.country?.data?.attributes?.name} city={cityData?.cities?.data?.[0]?.attributes?.name}/>
            {(!cityLoading && cityData && checkDifferentPageLanguage(cityData, locale)) ? (
                <div className="flex flex-col justify-center mx-auto">
                    <div className="flex justify-center mx-auto mt-5 mr-2 ml-2">
                        <div className="flex flex-row bg-quaternary p-2 rounded-xl">
                            <div className="flex flex-row items-center gap-2 cursor-pointer">
                                <p>{t('city.language_not_available', { current_locale: t(`language.${getLanguageNameFromLocale(locale)}`)})}</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-center">
                        <Link href={{pathname: "/destinations"}}>
                            <button className="bg-primary text-white p-2 rounded-lg mt-5">{t('common.back')}</button>
                        </Link>
                    </div>
                </div>
            ) : (
                <>
                    <div className='relative max-w-[80rem] mx-auto mt-5 sm:mt-10 font-poppins'>
                        <div className='flex flex-col mx-auto mr-5 ml-5 justify-center gap-5'>
                            <div className="flex flex-col gap-2 mb-3">
                                <div className="flex flex-col">
                                    {pageTitle({title: cityData?.cities?.data?.[0]?.attributes?.name})}
                                </div>
                                <p className="leading-8 text-gray-600">
                                    {cityData?.cities?.data?.[0]?.attributes?.description}
                                </p>
                            </div>
                            <CityContentList cityName={name as string} locale={currentLocale as string}/>
                        </div>
                    </div>
                </>
            )}
        </>
    )
}

export const getStaticProps: GetStaticProps = async (context) => {
    const client = getApolloClient()
    const locale = context.locale
    const cityName = context.params?.name as string

    try {
        const { data } = await client.query({
            query: GET_CITIES,
            variables: {
                "locale": locale,
                "cityName": cityName,
            }
        })

        // Extract the city data or provide a default/fallback
        const cityData = data.cities.data.length > 0 ? data : null

        return {
            props: {
                ...(await serverSideTranslations(locale, ['common', 'navbar', 'post'])),
                cityData,
            }
        }
    } catch (error) {
        console.error('Error fetching cities:', error)

        // Return empty props or an error message
        return {
            props: {
                ...(await serverSideTranslations(locale, ['common', 'navbar', 'post'])),
                cityData: null,
                error: 'Failed to fetch city data',
            },
        }
    }
}

export const getStaticPaths: GetStaticPaths<{ name: string }> = async () => {
    return {
        paths: [],
        fallback: 'blocking'
    }
}